import React from 'react';
import { ChevronLeft } from 'lucide-react';

export const PAGE_HEADER_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

const ROUNDED = {
  card: 'rounded-t-xl',
  standalone: 'rounded-xl',
  none: '',
};

const BUTTON_COLORS = {
  blue: 'text-blue-600 hover:bg-blue-50',
  green: 'text-green-600 hover:bg-green-50',
  purple: 'text-purple-600 hover:bg-purple-50',
  indigo: 'text-indigo-600 hover:bg-indigo-50',
  sky: 'text-sky-700 hover:bg-sky-50',
  red: 'text-red-600 hover:bg-red-50',
};

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export function PageHeaderButton({
  children,
  icon,
  color = 'blue',
  variant = 'solid',
  className = '',
  as: Component = 'button',
  type,
  ...props
}) {
  const isGhost = variant === 'ghost' || variant === 'icon';
  const styles = isGhost
    ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
    : `bg-white ${BUTTON_COLORS[color] || BUTTON_COLORS.blue}`;
  const sizing = variant === 'icon' ? 'p-2' : 'px-3 py-1.5';
  const buttonType = Component === 'button' ? (type || 'button') : type;

  return (
    <Component
      type={buttonType}
      className={`${BUTTON_BASE} ${sizing} ${styles} ${className}`.trim()}
      {...props}
    >
      {icon}
      {children}
    </Component>
  );
}

export function PageHeaderBackButton({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex-shrink-0 rounded-lg bg-white/20 p-1.5 text-white transition-colors hover:bg-white/30"
    >
      <ChevronLeft className="h-5 w-5 text-white" />
    </button>
  );
}

export function PageHeaderStat({ label, value, className = '' }) {
  return (
    <div className={`text-right ${className}`.trim()}>
      <div className="text-xs text-blue-100">{label}</div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

export function PageHeaderSelect({ className = '', ...props }) {
  return (
    <select
      className={`w-full rounded-lg border-0 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 ${className}`.trim()}
      {...props}
    />
  );
}

export function PageHeaderField({ label, children }) {
  return (
    <label className="text-xs font-semibold text-white">
      {label}
      {children}
    </label>
  );
}

export const PAGE_HEADER_CONTROL_CLASS =
  'mt-1 block w-full rounded-lg border-0 bg-white px-3 py-1.5 text-sm font-medium text-blue-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/50';

export const PAGE_SHELL_TOP = 8;
export const PAGE_HEADER_BELOW = 'mb-2.5';

export function PageShell({ children, className = '', fill = true, style }) {
  return (
    <div
      className={[
        'flex flex-col',
        fill ? 'h-[calc(100vh-64px)] overflow-hidden' : 'min-h-[calc(100vh-64px)]',
        className,
      ].filter(Boolean).join(' ')}
      style={{ paddingTop: PAGE_SHELL_TOP, ...style }}
    >
      <div className="mx-auto flex h-full min-h-0 w-full max-w-[1920px] flex-1 flex-col px-3 pb-2 sm:px-6 lg:px-8">
        {children}
      </div>
    </div>
  );
}

export function PageCard({ children, className = '', overflow = true }) {
  return (
    <div className={`flex min-h-0 flex-1 flex-col rounded-xl border border-gray-200/80 bg-white shadow-xs ${overflow ? 'overflow-y-auto' : ''} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function PageBody({ children, className = '' }) {
  return (
    <div className={`min-h-0 flex-1 overflow-y-auto ${className}`.trim()}>
      {children}
    </div>
  );
}

export default function PageHeader({
  title,
  subtitle,
  extra,
  leading,
  actions,
  children,
  variant = 'card',
  className = '',
  actionsClassName = '',
}) {
  const right = actions ?? children;
  const rounding = ROUNDED[variant] || ROUNDED.card;
  const shadow = variant === 'standalone' ? 'shadow-xs' : '';

  return (
    <div
      className={`shrink-0 px-4 py-2.5 text-white ${rounding} ${shadow} ${className}`.trim()}
      style={{ background: PAGE_HEADER_GRADIENT }}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {leading}
          <div className="min-w-0">
            <h1 className="mb-0.5 flex flex-wrap items-center gap-2 text-lg font-semibold leading-tight text-white">
              {title}
            </h1>
            {subtitle ? <p className="text-xs text-blue-100">{subtitle}</p> : null}
            {extra}
          </div>
        </div>
        {right ? (
          <div className={`flex flex-wrap items-center gap-2 shrink-0 ${actionsClassName}`.trim()}>
            {right}
          </div>
        ) : null}
      </div>
    </div>
  );
}
